/**
 * Mindmap View - Centralized State Container
 *
 * CENTRALIZED STATE - all mindmap modules import from here.
 * This prevents circular imports and makes state visible/testable.
 */

// ============================================================
// DOM References
// ============================================================
export let container = null;
export let world = null;
export let svg = null;
export let canvas = null;

// ============================================================
// Injected Callbacks
// ============================================================
export let callbacks = null;
export let currentState = null;

// ============================================================
// Transform State (zoom/pan)
// ============================================================
export let mState = { zoom: 1, panX: 0, panY: 0 };
export let lastSavedTransform = { zoom: 1, panX: 0, panY: 0 };
export let transformSaveTimeout = null;

// ============================================================
// Layout State
// ============================================================
export let gapPercent = 50; // DEFAULT_GAP_PERCENT
// Level-0 side placement: "balanced" | "right" | "left". Synced from
// Store.mindmapLayoutMode on every render; a per-doc local preference.
export let layoutMode = "balanced";

// ============================================================
// Rendering State
// ============================================================
export let measurementCanvas = null;
export let measurementCtx = null;
export let renderScheduled = false;
// Dimension logs are a debug aid — on a 700-node map they fire thousands of
// times per layout pass and measurably slow rendering. Off by default; flip
// via setSuppressDimensionLogs(false) from the console when debugging sizing.
export let suppressDimensionLogs = true;

// ============================================================
// Interaction State
// ============================================================
export let hoveredMindmapId = null;
export let isDraggingNode = false;

// ============================================================
// Lifecycle Flags
// ============================================================
export let listenersAttached = false;
export let warnedMissingCallbacks = false;
export let eventUnsubscribers = [];

// ============================================================
// Setters (use these, don't mutate directly)
// ============================================================
export function setContainer(el) { container = el; }
export function setWorld(el) { world = el; }
export function setSvg(el) { svg = el; }
export function setCanvas(el) { canvas = el; }
export function setCallbacks(cbs) { callbacks = cbs; }
export function setCurrentState(state) { currentState = state; }
export function setMState(newState) { mState = { ...mState, ...newState }; }
export function setLastSavedTransform(t) { lastSavedTransform = { ...t }; }
export function setTransformSaveTimeout(t) { transformSaveTimeout = t; }
export function setGapPercent(g) { gapPercent = g; }
export function setLayoutMode(m) { layoutMode = m; }
export function setMeasurementCanvas(c) { measurementCanvas = c; }
export function setMeasurementCtx(ctx) { measurementCtx = ctx; }
export function setRenderScheduled(v) { renderScheduled = v; }
export function setSuppressDimensionLogs(v) { suppressDimensionLogs = v; }
export function setHoveredMindmapId(id) { hoveredMindmapId = id; }
export function setIsDraggingNode(v) { isDraggingNode = v; }

// Memoized "is visible" lookup. Cleared in clearVisibilityCache(), called
// from render.js at the start of each render so the cache reflects the
// current hide-filter settings without stale entries.
const visibilityCache = new Map();
let tagFilterCache = null;
export function clearVisibilityCache() { visibilityCache.clear(); tagFilterCache = null; }

/** The render pass's tag filter (engine/core/tag-filter.js), built on first use. */
export function getTagFilter() {
  tagFilterCache ??= callbacks?.makeTagFilter?.() || null;
  return tagFilterCache;
}

/**
 * Filter a node's children by hideCompleted and hidden tags. Two rules
 * compose, in priority order:
 *
 *   1. DONE-CASCADE. If Done is currently hidden (legacy hideCompleted=true,
 *      OR the Done tag id is in hiddenTagIds), a Done node hides its entire
 *      subtree — descendants don't get a chance to override. Lets the user
 *      "close a topic" by marking the parent Done.
 *
 *   2. ANCESTOR-AWARE for everything else. A node hidden by some other tag
 *      stays visible if it has any visible descendant — keeps the path to a
 *      matching leaf reachable even when an ancestor would otherwise filter.
 *
 * @param {Object} node
 * @returns {Array}
 */
export function getVisibleChildren(node) {
  if (!node.children) return [];
  const hide = !!currentState?.hideCompleted;
  const hiddenTagIds = callbacks?.getHiddenTagIds?.() || [];
  const hasHiddenTags = hiddenTagIds.length > 0;
  const outOfHorizon = callbacks?.getHorizonPredicate?.() || null;
  if (!hide && !hasHiddenTags && !outOfHorizon) return node.children;

  const doneTagId = callbacks?.getDoneTagId?.();
  const doneHidden = hide || (!!doneTagId && hiddenTagIds.includes(doneTagId));
  const isDone = (n) => callbacks?.nodeIsDone?.(n);

  const isSelfHidden = (n) => {
    if (hide && isDone(n)) return true;
    if (outOfHorizon && outOfHorizon(n)) return true;
    if (getTagFilter()?.isHidden(n, hiddenTagIds)) return true;
    return false;
  };

  const isVisible = (n) => {
    if (visibilityCache.has(n.id)) return visibilityCache.get(n.id);
    let visible;
    if (doneHidden && isDone(n)) {
      // Cascade: Done node with Done hidden disappears with all its subtree.
      visible = false;
    } else if (!isSelfHidden(n)) {
      visible = true;
    } else {
      // Self-hidden by a non-Done filter — keep the parent visible if any
      // descendant survives, so the path to that descendant stays open.
      visible = (n.children || []).some(isVisible);
    }
    visibilityCache.set(n.id, visible);
    return visible;
  };

  return node.children.filter(isVisible);
}
export function setListenersAttached(v) { listenersAttached = v; }
export function setWarnedMissingCallbacks(v) { warnedMissingCallbacks = v; }
export function addEventUnsubscriber(fn) { eventUnsubscribers.push(fn); }
export function clearEventUnsubscribers() {
  eventUnsubscribers.forEach(fn => fn());
  eventUnsubscribers = [];
}

// ============================================================
// Reset (called on destroy)
// ============================================================
export function resetState() {
  container = null;
  world = null;
  svg = null;
  canvas = null;
  callbacks = null;
  currentState = null;
  mState = { zoom: 1, panX: 0, panY: 0 };
  lastSavedTransform = { zoom: 1, panX: 0, panY: 0 };
  if (transformSaveTimeout) clearTimeout(transformSaveTimeout);
  transformSaveTimeout = null;
  gapPercent = 50;
  measurementCanvas = null;
  measurementCtx = null;
  renderScheduled = false;
  suppressDimensionLogs = true;
  hoveredMindmapId = null;
  isDraggingNode = false;
  listenersAttached = false;
  warnedMissingCallbacks = false;
  eventUnsubscribers.forEach(fn => fn());
  eventUnsubscribers = [];
}
